/**
 * Given an orientation and gap, returns the CSS styles to apply to the Frame Layout element.
 * This function is also used by the Tooling overlay for Frames to mirror the Frame Layout.
 *
 * The layout has at most two axes: the main flow axis (set by `orientation`)
 * and the single perpendicular cross axis. `crossAxisTrackCount` is the number
 * of tracks (rows/columns) on that one cross axis. When it is
 * `> 1`, the otherwise single-axis flow wraps into a 2D grid; a value of `1`
 * (or omitted) keeps the original single-row / single-column behavior.
 */
export const getGridFrameLayoutCSS = (
  orientation: 'vertical' | 'horizontal',
  gap: number,
  crossAxisTrackCount?: number
) => {
  const base = {
    display: 'grid',
    gridAutoFlow: orientation === 'horizontal' ? 'column' : 'row',
    gap: `${gap}px`,
  };

  if (!crossAxisTrackCount || crossAxisTrackCount <= 1) {
    return base;
  }

  // For vertical flow (gridAutoFlow: row) the cross axis is columns; for
  // horizontal flow (gridAutoFlow: column) it is rows. Fixing the
  // perpendicular track count makes items wrap into a 2D grid.
  return orientation === 'horizontal'
    ? {...base, gridTemplateRows: `repeat(${crossAxisTrackCount}, max-content)`}
    : {
        ...base,
        gridTemplateColumns: `repeat(${crossAxisTrackCount}, max-content)`,
      };
};
